import { UIButton, UIColor } from "uicore-ts"

import { BSColor } from "./BSColor"
import { RHColor } from "./RHColor"


// MARK: - Types
export type RHBarButtonVariant = "default" | "callToAction" | "outlineOnDarkBackground"


// MARK: - RH Bar Button

export class RHBarButton extends UIButton {
    
    _variant: RHBarButtonVariant = "default"
    
    constructor(elementID?: string) {
        
        super(elementID);
        
        // Configure Static CSS (once) using the cast workaround
        (this as RHBarButton).configureWithObject({
            style: {
                borderRadius: "6px",
                textDecoration: "none",
                // Explicit transitions ONLY for visual properties.
                transition: "background-color 0.15s ease," +
                    " color 0.15s ease, " +
                    "border-color 0.15s ease, " +
                    "border-width 0.15s ease"
            }
        })
        
        // Initialize colors for default variant
        this.refreshColorConfiguration()
        
    }
    
    
    // MARK: - Color Management
    
    set variant(value: RHBarButtonVariant) {
        this._variant = value
        this.refreshColorConfiguration()
        this.updateContentForCurrentState()
    }
    
    get variant() {
        return this._variant
    }
    
    
    private refreshColorConfiguration() {
        
        let colorConfig: any
        
        if (this.variant === "default") {
            
            // --- Default Variant ---
            colorConfig = {
                titleLabel: {
                    normal: BSColor.dark,
                    hovered: BSColor.primary,
                    highlighted: BSColor.primary,
                    selected: BSColor.warningOrange
                },
                background: {
                    normal: UIColor.transparentColor,
                    hovered: BSColor.primary.colorWithAlpha(0.1),
                    highlighted: BSColor.primary.colorWithAlpha(0.2),
                    selected: BSColor.warningOrange.colorWithAlpha(0.05)
                }
            }
            
        }
        else if (this.variant === "outlineOnDarkBackground") {
            
            // --- Outline Variant ---
            // Matches Snippet 1 (Normal): Transparent bg, White Text, Border 0.3
            // Matches Snippet 2 (Hover): 0.15 White Bg, White Text, Border 0.5
            colorConfig = {
                titleLabel: {
                    normal: UIColor.whiteColor,
                    hovered: UIColor.whiteColor,       // Stays white on hover
                    highlighted: UIColor.whiteColor,   // Stays white on highlight
                    selected: BSColor.warning
                },
                background: {
                    normal: UIColor.transparentColor,
                    hovered: UIColor.whiteColor.colorWithAlpha(0.15), // Slight white fill on hover
                    highlighted: UIColor.whiteColor.colorWithAlpha(0.25), // Slightly darker on click
                    selected: BSColor.warning.colorWithAlpha(0.05)
                }
            }
            
        }
        else {
            
            // --- Call To Action Variant ---
            colorConfig = {
                titleLabel: {
                    normal: UIColor.whiteColor,
                    hovered: UIColor.whiteColor,
                    highlighted: UIColor.whiteColor,
                    selected: BSColor.warning
                },
                background: {
                    normal: RHColor.callToActionColor,
                    hovered: RHColor.callToActionColor.colorByMultiplyingRGB(0.9),
                    highlighted: RHColor.callToActionColor.colorByMultiplyingRGB(0.8),
                    selected: BSColor.warning.colorWithAlpha(0.05)
                }
            }
            
        }
        
        // Use configureWithObject to update colors without replacing the object
        (this as RHBarButton).configureWithObject({ colors: colorConfig })
        
    }
    
    
    // MARK: - State Updates (CSS Only)
    
    override updateContentForNormalState() {
        
        super.updateContentForNormalState()
        
        if (this.variant === "outlineOnDarkBackground") {
            // Snippet 1: border-color: rgba(255, 255, 255, 0.3)
            this.style.border = "1px solid rgba(255, 255, 255, 0.3)"
        }
        else {
            this.style.border = "none"
        }
        
        this.titleLabel.style.fontWeight = "500"
        
    }
    
    
    override updateContentForHoveredState() {
        
        super.updateContentForHoveredState()
        
        // Snippet 2: Border is still present but opacity increases to 0.5
        if (this.variant === "outlineOnDarkBackground") {
            this.style.border = "1px solid rgba(255, 255, 255, 0.5)"
        }
        else {
            this.style.border = "none"
        }
        
        this.titleLabel.style.fontWeight = "500"
        
    }
    
    
    override updateContentForHighlightedState() {
        
        super.updateContentForHighlightedState()
        
        // Logic: Keep border, further increase opacity for pressed state
        if (this.variant === "outlineOnDarkBackground") {
            this.style.border = "1px solid rgba(255, 255, 255, 0.7)"
        }
        else {
            this.style.border = "none"
        }
        
        this.titleLabel.style.fontWeight = "400"
        
    }
    
    
    override updateContentForSelectedState() {
        
        super.updateContentForSelectedState()
        
        // Note: The logout button typically wouldn't have a selected state,
        // but we maintain the class's selected state logic.
        if (this.variant === "outlineOnDarkBackground") {
            // Optional: Change border style for selected outline button
            this.style.border = `2px solid ${BSColor.warning.stringValue}`
        }
        else {
            // Apply Warning Border for Selected state (default behavior)
            this.style.border = `2px solid ${BSColor.warningOrange.stringValue}`
        }
        
        this.titleLabel.style.fontWeight = "500"
        
    }
    
}
